// The SVP profile plot — the console's sound-velocity figure, as a pure function.
//
// WHY IT LIVES HERE. This was inline in main.ts, which was fine while the map
// console was the only thing that drew it. The printable report now offers the
// profile too, and a report that drew its own would be the failure the whole
// codebase is arranged against: a second renderer that eventually disagrees with
// the screen about where the thermocline is, handed to the client on paper.
//
// It is pure — cell in, SVG string out. No DOM, no globals, no fetch. That is
// what makes it usable from both a live panel and a document generator, and it
// is why the depth-range zoom is a PARAMETER rather than module state: the
// console passes its control's value, the exports and the report pass null.
//
// UNITS ARE SI, DELIBERATELY, AND DO NOT FOLLOW THE IMPERIAL TOGGLE. Depths here
// pair with m/s sound speeds and with the SI CSV the survey software ingests
// (see the note at the top of ui/units.ts). A profile plotted in feet beside a
// sound speed in metres per second is not a convenience, it is a trap.

import { soundSpeed, SOUND_SPEED_EQS, type SoundSpeedEq } from "../forecast/soundspeed.js";
import type { ArgoProfile } from "../forecast/argo.js";
import type { SvpCell, SvpClimatology } from "../data/api.js";

// Sound-speed structure line for the location primer — the survey-acoustics read
// (thermocline the MBES/USBL party plans SV casts around). Planning-grade, and
// labelled as such: a modelled profile is a first guess, not the on-site cast.
// Round depth ticks up to a visible floor — coarser as the axis deepens.
export function svpDepthTicks(dmax: number): number[] {
  const step = dmax <= 200 ? 50 : dmax <= 500 ? 100 : dmax <= 1200 ? 200 : 500;
  const out: number[] = [];
  for (let d = step; d <= dmax + 1; d += step) out.push(d);
  // The frame floor gets its own labelled tick when the last round tick stops
  // short of it (a full-column axis rarely lands on a round step), unless it
  // would crowd the neighbour.
  if (out.length === 0 || dmax - out[out.length - 1] >= step / 2) out.push(dmax);
  return out;
}

// A compact sound-speed / temperature profile for the SVP workspace. Depth on
// Y increasing downward (oceanographic convention), spanning the FULL water
// column. It first shipped zoomed to the upper-ocean thermocline; Les's review
// (2026-07-21) rejected that — a profile plot that stops at 200 m over an 828 m
// site reads as wrong data, not as a zoom. The thermocline stays legible through
// its band + MLD line, drawn features rather than axis policy. The depth-range
// control (Les, 2026-07-27) is the sanctioned exception: an explicit cut the
// reader chose from a labelled control, defaulting to the full column.
// Sound speed (cool, --m1) and temperature (warm, --m3) share the plot on paired
// scales so the reader sees the temperature step drive the sound-speed step.
// Inline SVG: drops into the panel body's innerHTML with no canvas lifecycle, and
// the classes carry every colour so it re-casts across day/dusk/night via CSS tokens.
// Standard oceanographic profile display: three side-by-side panels — temperature,
// salinity, sound velocity — sharing one depth-down Y axis, with the thermocline
// band, mixed-layer depth and sound-channel (SOFAR) axis carried across. Sound
// velocity is recomputed from the stored T/S/D under the chosen equation `eq`.
// Row guard shared by the renderer and the frame-floor helper: depth, temperature
// and salinity all finite (column 4, the stored SV, is recomputed under `eq` and
// never trusted from the wire).
export const svpFiniteRow = (r: number[]): boolean =>
  r.length >= 4 && Number.isFinite(r[0]) && Number.isFinite(r[1]) && Number.isFinite(r[2]);

// Full-column frame floor: deepest drawn data — the model's bottom level or the
// float's full cast — extended by the NCEI charted depth, rounded up to 50 m.
// Shared by the renderer and the depth-range control so presets are judged
// against exactly the axis the unzoomed chart would draw.
export function svpFullDepthM(c: SvpCell, obs: ArgoProfile | null, siteDepthM: number | null): number {
  const rows = c.p.filter(svpFiniteRow);
  if (rows.length === 0) return 0;
  let d = rows[rows.length - 1][0];
  if (obs !== null && Number.isFinite(obs.maxDepthM)) d = Math.max(d, obs.maxDepthM);
  if (siteDepthM !== null && Number.isFinite(siteDepthM) && siteDepthM > 0) d = Math.max(d, siteDepthM);
  return Math.ceil(d / 50) * 50;
}

// Depth-range zoom presets (Les, 2026-07-27: "focus on a specific depth range
// rather than all the way to the bottom"). Upper-ocean cuts from the surface; a
// preset is offered only when the full column reaches PAST it, so a shelf site
// never shows a cut deeper than its own seabed.
export const SVP_ZOOM_PRESETS: readonly number[] = [200, 500, 1000];

interface SvpPanelSpec {
  x0: number;
  lo: number;
  hi: number;
  min: number;
  max: number;
  unit: string;
  title: string;
  cls: string;
  vals: number[];
  /** Observed (Argo) values + their own depths, drawn as a second curve on the
   *  same axis. Empty when there is no float overlay. */
  obsVals: number[];
  obsDepths: number[];
  /** Climatology (WOA23) values + their own depths, drawn as a muted reference
   *  curve on the same axis. Empty when there is no climatology block. */
  climVals: number[];
  climDepths: number[];
  fmt: (v: number) => string;
}

// Draws up to three profiles — Bender's trio: the modelled FORECAST (CMEMS, solid),
// the MEASURED cast (Argo float, dashed) when one is in hand, and the USUAL climatology
// (WOA23 monthly average, dashed and muted) when the block is present. The console's
// standing rule holds: observation and reference sit next to model, never blended. All
// sound-velocity curves are computed with the SAME soundSpeed(eq, …); if they used
// different equations the gaps between them would be an artefact of the maths rather
// than the sea. Every curve shares the depth axis, which runs to the DEEPEST drawn
// data — the model's bottom level or the float's full cast, whichever reaches
// further (Les, 2026-07-21: the axis must cover the water column, not a window).
// Climatology alone doesn't stretch the axis: it reaches 1500 m everywhere, which
// would flatten every shelf profile against a reference curve nobody is surveying.
// The NCEI charted depth, when known, extends the axis too and is drawn as a
// seabed line — the one number the surveyor holds that the model cell may not reach.
// `zoomM` clamps the frame to an upper-ocean cut (a chosen depth-range preset);
// null draws the full column. View state only — the exports always pass null.
export function svpProfilesSvg(
  c: SvpCell,
  eq: SoundSpeedEq,
  obs: ArgoProfile | null,
  clim: SvpClimatology | null,
  siteDepthM: number | null,
  zoomM: number | null,
): string {
  const rows = c.p.filter(svpFiniteRow);
  if (rows.length < 3) return "";

  const fullD = svpFullDepthM(c, obs, siteDepthM);
  if (!(fullD > 0)) return "";
  // Depth-range zoom (Les, 2026-07-27): the frame floor becomes the chosen cut,
  // and everything below — ticks, curves, annotations — hangs off dmax as before.
  const zoomed = zoomM !== null && zoomM < fullD;
  const dmax = zoomed && zoomM !== null ? zoomM : fullD;

  // Model rows within the frame. Unzoomed, dmax covers the model's bottom level
  // (it is one of fullD's inputs) and every row is kept; zoomed, the break keeps
  // the first row PAST the floor so the curve exits the frame rather than
  // stopping short of it.
  const vis: number[][] = [];
  for (const r of rows) {
    vis.push(r);
    if (r[0] >= dmax) break;
  }
  if (vis.length < 2) return "";

  const temps = vis.map((r) => r[1]);
  const sals = vis.map((r) => r[2]);
  // Sound velocity over the FULL profile (vis is a prefix of rows): the drawn
  // curve only needs the visible prefix, but the SOFAR axis below must be found
  // on the whole column — a zoomed frame must not promote its local minimum to
  // "the channel axis".
  const svAll = rows.map((r) => soundSpeed(eq, r[1], r[2], r[0]));
  const sv = svAll.slice(0, vis.length);

  // Observed float levels within the same depth window, plus the first crossing
  // level so the curve reaches the floor of the frame just as the modelled one
  // does. Sound velocity uses the SAME equation as the modelled curve above.
  const obsVis: { d: number; t: number; s: number; sv: number }[] = [];
  if (obs !== null) {
    for (const lv of obs.levels) {
      if (!Number.isFinite(lv.depthM) || !Number.isFinite(lv.tempC) || !Number.isFinite(lv.salPSU)) continue;
      obsVis.push({ d: lv.depthM, t: lv.tempC, s: lv.salPSU, sv: soundSpeed(eq, lv.tempC, lv.salPSU, lv.depthM) });
      if (lv.depthM >= dmax) break;
    }
  }
  const drawObs = obsVis.length >= 2;
  const obsTemps = drawObs ? obsVis.map((r) => r.t) : [];
  const obsSals = drawObs ? obsVis.map((r) => r.s) : [];
  const obsSv = drawObs ? obsVis.map((r) => r.sv) : [];
  const obsDepths = drawObs ? obsVis.map((r) => r.d) : [];

  // Climatology (WOA23) levels, same treatment as the float: clamp to the shared
  // window, keep the first crossing level so the curve reaches the floor, derive SV
  // under the same equation. Non-finite rows are already dropped upstream (api.ts).
  const climVisRows: { d: number; t: number; s: number; sv: number }[] = [];
  if (clim !== null) {
    for (const lv of clim.levels) {
      const [d, t, s] = lv;
      climVisRows.push({ d, t, s, sv: soundSpeed(eq, t, s, d) });
      if (d >= dmax) break;
    }
  }
  const drawClim = climVisRows.length >= 2;
  const climTemps = drawClim ? climVisRows.map((r) => r.t) : [];
  const climSals = drawClim ? climVisRows.map((r) => r.s) : [];
  const climSv = drawClim ? climVisRows.map((r) => r.sv) : [];
  const climDepths = drawClim ? climVisRows.map((r) => r.d) : [];

  const extent = (vals: number[]): [number, number] => {
    let lo = Infinity, hi = -Infinity;
    for (const v of vals) {
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    return [lo, hi];
  };
  const pad = (lo: number, hi: number, frac: number, floor: number): [number, number] => {
    const p = Math.max((hi - lo) * frac, floor);
    return [lo - p, hi + p];
  };
  // Extents span EVERY drawn curve so none is clipped off the side of its panel —
  // an overlay you cannot see is worse than none.
  const [tMin, tMax] = extent([...temps, ...obsTemps, ...climTemps]);
  const [sMin, sMax] = extent([...sals, ...obsSals, ...climSals]);
  const [cMin, cMax] = extent([...sv, ...obsSv, ...climSv]);
  const [tLo, tHi] = pad(tMin, tMax, 0.12, 0.3);
  const [sLo, sHi] = pad(sMin, sMax, 0.12, 0.05);
  const [cLo, cHi] = pad(cMin, cMax, 0.12, 0.5);

  // Sound-channel (SOFAR) axis = depth of minimum sound velocity under this equation.
  let axisDepth = rows[0][0];
  let axisMin = svAll[0];
  for (let i = 1; i < svAll.length; i++) {
    if (svAll[i] < axisMin) {
      axisMin = svAll[i];
      axisDepth = rows[i][0];
    }
  }

  // A deep column gets a taller frame: the full-depth axis (above) trades vertical
  // resolution for coverage, and a fixed 232-unit frame would compress a 1000 m
  // profile's thermocline into a few pixels. A zoomed frame is always the tall
  // one — buying vertical resolution is the zoom's whole purpose.
  const W = 360, H = dmax > 500 || zoomed ? 320 : 232, mL = 30, mR = 8, mT = 30, mB = 30, gap = 16;
  const plotH = H - mT - mB;
  const y0 = mT;
  const pw = (W - mL - mR - 2 * gap) / 3;
  const yD = (d: number): number => y0 + (d / dmax) * plotH;

  const panels: SvpPanelSpec[] = [
    { x0: mL, lo: tLo, hi: tHi, min: tMin, max: tMax, unit: "°C", title: "Temp", cls: "svp-temp", vals: temps, obsVals: obsTemps, obsDepths, climVals: climTemps, climDepths, fmt: (v) => v.toFixed(0) },
    { x0: mL + pw + gap, lo: sLo, hi: sHi, min: sMin, max: sMax, unit: "PSU", title: "Sal", cls: "svp-sal", vals: sals, obsVals: obsSals, obsDepths, climVals: climSals, climDepths, fmt: (v) => v.toFixed(1) },
    { x0: mL + 2 * (pw + gap), lo: cLo, hi: cHi, min: cMin, max: cMax, unit: "m/s", title: "Sound vel", cls: "svp-sound", vals: sv, obsVals: obsSv, obsDepths, climVals: climSv, climDepths, fmt: (v) => v.toFixed(0) },
  ];

  const p: string[] = [];
  const ticks = svpDepthTicks(dmax);

  for (let pi = 0; pi < panels.length; pi++) {
    const pan = panels[pi];
    const xw = (v: number): number => pan.x0 + ((v - pan.lo) / (pan.hi - pan.lo)) * pw;
    // thermocline band — skipped once its top sinks below the frame floor
    if (c.th !== null && c.th[0] < dmax) {
      const yb = yD(Math.min(c.th[0], dmax));
      const ht = Math.max(0, yD(Math.min(c.th[1], dmax)) - yb);
      p.push(`<rect class="svp-band" x="${pan.x0.toFixed(1)}" y="${yb.toFixed(1)}" width="${pw.toFixed(1)}" height="${ht.toFixed(1)}"/>`);
    }
    // depth gridlines
    for (const d of ticks) {
      const y = yD(d);
      p.push(`<line class="svp-grid" x1="${pan.x0.toFixed(1)}" y1="${y.toFixed(1)}" x2="${(pan.x0 + pw).toFixed(1)}" y2="${y.toFixed(1)}"/>`);
    }
    // mixed-layer depth
    if (c.mld !== null && c.mld <= dmax) {
      const y = yD(c.mld);
      p.push(`<line class="svp-mld" x1="${pan.x0.toFixed(1)}" y1="${y.toFixed(1)}" x2="${(pan.x0 + pw).toFixed(1)}" y2="${y.toFixed(1)}"/>`);
    }
    // SOFAR axis (in the sound-velocity panel)
    if (pi === 2 && axisDepth <= dmax) {
      const y = yD(axisDepth);
      p.push(`<line class="svp-axisln" x1="${pan.x0.toFixed(1)}" y1="${y.toFixed(1)}" x2="${(pan.x0 + pw).toFixed(1)}" y2="${y.toFixed(1)}"/>`);
    }
    // seabed (NCEI charted depth) — the floor the SV cast is planned against. The
    // model cell's bottom level routinely stops short of it; drawing the real
    // seabed is what reconciles "deepest model level" with the depth the surveyor
    // knows (Les, 2026-07-21).
    if (siteDepthM !== null && Number.isFinite(siteDepthM) && siteDepthM > 0 && siteDepthM <= dmax) {
      const y = yD(siteDepthM);
      p.push(`<line class="svp-seabed" x1="${pan.x0.toFixed(1)}" y1="${y.toFixed(1)}" x2="${(pan.x0 + pw).toFixed(1)}" y2="${y.toFixed(1)}"/>`);
    }
    // climatology (WOA23) curve — drawn first of all so it sits UNDER both the
    // observed and modelled curves; muted + dashed, it reads as quiet reference. Its
    // own depths, clamped to the shared window (rows deeper than dmax are dropped).
    if (pan.climVals.length >= 2) {
      const cpts = pan.climVals
        .map((v, i) => `${xw(v).toFixed(1)},${yD(Math.min(pan.climDepths[i], dmax)).toFixed(1)}`)
        .join(" ");
      p.push(`<polyline class="svp-clim" points="${cpts}"/>`);
    }
    // observed (Argo) curve — drawn next so the modelled curve reads on top of it;
    // its own depths, clamped to the shared window (rows deeper than dmax are dropped).
    if (pan.obsVals.length >= 2) {
      const opts = pan.obsVals
        .map((v, i) => `${xw(v).toFixed(1)},${yD(Math.min(pan.obsDepths[i], dmax)).toFixed(1)}`)
        .join(" ");
      p.push(`<polyline class="svp-obs" points="${opts}"/>`);
    }
    // modelled curve — its one kept row past a zoomed floor clamps to the frame
    // edge, same as the observed and climatology crossing rows
    const pts = pan.vals.map((v, i) => `${xw(v).toFixed(1)},${yD(Math.min(vis[i][0], dmax)).toFixed(1)}`).join(" ");
    p.push(`<polyline class="${pan.cls}" points="${pts}"/>`);
    // titles + axis labels
    const cx = pan.x0 + pw / 2;
    p.push(`<text class="svp-ptitle" x="${cx.toFixed(1)}" y="${(mT - 16).toFixed(1)}" text-anchor="middle">${pan.title}</text>`);
    p.push(`<text class="svp-unit" x="${cx.toFixed(1)}" y="${(mT - 6).toFixed(1)}" text-anchor="middle">${pan.unit}</text>`);
    const yb = (H - mB + 12).toFixed(1);
    p.push(`<text class="svp-lab" x="${pan.x0.toFixed(1)}" y="${yb}" text-anchor="start">${pan.fmt(pan.min)}</text>`);
    p.push(`<text class="svp-lab" x="${(pan.x0 + pw).toFixed(1)}" y="${yb}" text-anchor="end">${pan.fmt(pan.max)}</text>`);
  }

  // panel separators — a clear vertical line between the three panels, not just
  // air (Les, 2026-07-21). Centred in each gap, spanning the plot rows.
  for (let gi = 1; gi <= 2; gi++) {
    const x = mL + gi * pw + (gi - 0.5) * gap;
    p.push(`<line class="svp-sep" x1="${x.toFixed(1)}" y1="${(mT - 4).toFixed(1)}" x2="${x.toFixed(1)}" y2="${(mT + plotH).toFixed(1)}"/>`);
  }

  // seabed label, once, at the frame's right edge (the line itself repeats per panel)
  if (siteDepthM !== null && Number.isFinite(siteDepthM) && siteDepthM > 0 && siteDepthM <= dmax) {
    const y = yD(siteDepthM);
    p.push(`<text class="svp-seabed-lab" x="${(W - mR).toFixed(1)}" y="${(y - 3).toFixed(1)}" text-anchor="end">seabed ${Math.round(siteDepthM)} m</text>`);
  }

  // depth axis labels (far left)
  for (const d of ticks) {
    const y = yD(d);
    p.push(`<text class="svp-ax" x="${(mL - 4).toFixed(1)}" y="${(y + 3).toFixed(1)}" text-anchor="end">${d}</text>`);
  }
  p.push(`<text class="svp-ax" x="${(mL - 4).toFixed(1)}" y="${(mT - 6).toFixed(1)}" text-anchor="end">m</text>`);

  return `<svg class="svp-svg svp-svg3" viewBox="0 0 ${W} ${H}" role="img" aria-label="Temperature, salinity and sound-velocity profiles versus depth">${p.join("")}</svg>`;
}

// Legend key: an inline sample drawn with the SAME class the plot uses, so dash
// pattern, weight and colour cannot drift from the curve it names. The previous
// keys were CSS `border-top: dashed`, which renders the float's 4-3 dashes and the
// climatology's 2-3 dots as the same generic dash — exactly the "which line is
// which" confusion Les reported (2026-07-21).
export const svpLegendKey = (cls: string): string =>
  cls === "svp-band"
    ? `<svg class="svp-lgkey" viewBox="0 0 20 8" aria-hidden="true"><rect class="svp-band" x="1" y="0" width="18" height="8"/></svg>`
    : `<svg class="svp-lgkey" viewBox="0 0 20 8" aria-hidden="true"><line class="${cls}" x1="1" y1="4" x2="19" y2="4"/></svg>`;

export const eqLabel = (eq: SoundSpeedEq): string =>
  SOUND_SPEED_EQS.find((e) => e.key === eq)?.label ?? eq;
